import { computed, Injectable, signal } from '@angular/core'
import { CoachmarkStartOptions, CoachmarkStep, CoachmarkTour } from '@models/coachmark.model'

interface ActiveTour {
  id: string
  steps: CoachmarkStep[]
}

/** Persisted shape: `{ [tourId]: { [stepKey]: true } }`. */
type SeenMap = Record<string, Record<string, boolean>>

/**
 * Drives reusable, page-agnostic product tours ("coachmarks").
 *
 * Usage from a page/component:
 * ```ts
 * private coachmark = inject(CoachmarkService)
 * this.coachmark.startWhenReady({ id: 'reels', steps: [...] })
 * ```
 *
 * The rendering (dark overlay with a spotlight hole + tooltip) is handled by the
 * single global {@link CoachmarkOverlayComponent}, which reacts to this service's
 * signals. Targets are located via `data-coachmark="<key>"` attributes or raw CSS
 * selectors.
 *
 * Seen state is tracked **per step**, not per tour, under a single localStorage
 * object. This matters because a step's target may be absent the first time a
 * screen is visited (e.g. an asset preview without a transcript, or a chat
 * without a Clips tab) and only appear later — those steps must still be shown
 * the first time they actually become available. Each step is keyed by its
 * `target`, which is unique within a tour, e.g.:
 * ```json
 * { "asset-preview": { "apm-edit-name": true, "apm-reason": true } }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class CoachmarkService {
  private static readonly STORAGE_KEY = 'mh_coachmarks'

  private readonly seen = signal<SeenMap>(this.loadSeen())
  private readonly active = signal<ActiveTour | null>(null)
  private readonly index = signal<number>(0)

  /** Token to invalidate in-flight `startWhenReady` retry loops. */
  private startToken = 0

  /** Whether a tour is currently being displayed. */
  readonly isActive = computed(() => this.active() !== null)

  /** The step currently being shown, or `null`. */
  readonly currentStep = computed<CoachmarkStep | null>(() => {
    const tour = this.active()
    return tour ? (tour.steps[this.index()] ?? null) : null
  })

  /** Zero-based index of the current step. */
  readonly currentIndex = this.index.asReadonly()

  /** Total number of (visible) steps in the active tour. */
  readonly stepCount = computed(() => this.active()?.steps.length ?? 0)

  readonly isFirstStep = computed(() => this.index() === 0)
  readonly isLastStep = computed(() => this.index() >= this.stepCount() - 1)

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** True if the given step of a tour has already been shown. */
  hasSeenStep(tourId: string, stepKey: string): boolean {
    return this.seen()[tourId]?.[stepKey] === true
  }

  /** True if every step of a tour has already been shown. */
  isTourFullySeen(tour: CoachmarkTour): boolean {
    return tour.steps.every(step => this.hasSeenStep(tour.id, step.target))
  }

  /**
   * Starts a tour immediately, snapshotting the steps that are currently visible
   * AND not yet seen. If none qualify (or every step was already seen and
   * `force` is not set), nothing happens.
   */
  start(tour: CoachmarkTour, options: CoachmarkStartOptions = {}): boolean {
    const steps = tour.steps.filter(step => {
      if (!options.force && this.hasSeenStep(tour.id, step.target)) return false
      return this.isStepVisible(step)
    })
    if (steps.length === 0) return false

    // Replace any running tour.
    this.startToken++
    this.active.set({ id: tour.id, steps })
    this.index.set(0)
    this.markStepSeen(tour.id, steps[0].target)
    return true
  }

  /**
   * Like {@link start} but keeps retrying for `readyTimeout` ms until at least
   * one unseen target becomes visible. Handy right after navigation, while the
   * target view is still rendering its data.
   */
  startWhenReady(tour: CoachmarkTour, options: CoachmarkStartOptions = {}): void {
    if (!options.force && this.isTourFullySeen(tour)) return

    const token = ++this.startToken
    const deadline = Date.now() + (options.readyTimeout ?? 2000)

    const attempt = (): void => {
      // A newer start() / startWhenReady() superseded this one.
      if (token !== this.startToken) return
      if (this.start(tour, { ...options, readyTimeout: undefined })) return
      if (Date.now() >= deadline) return
      setTimeout(attempt, 150)
    }

    // Defer one tick so the caller's view has a chance to render first.
    setTimeout(attempt, 0)
  }

  /** Advances to the next step, completing the tour at the end. */
  next(): void {
    const tour = this.active()
    if (!tour) return
    if (this.index() >= tour.steps.length - 1) {
      this.complete()
    } else {
      this.index.update(i => i + 1)
      this.markStepSeen(tour.id, tour.steps[this.index()].target)
    }
  }

  /** Goes back to the previous step (no-op on the first step). */
  previous(): void {
    this.index.update(i => Math.max(0, i - 1))
  }

  /** Completes the active tour. Shown steps were persisted as they appeared. */
  complete(): void {
    this.clear()
  }

  /**
   * Dismisses the active tour. Every step in the current snapshot is marked seen
   * so an explicit dismissal isn't shown again — steps that never appeared this
   * time remain unseen and can still surface later.
   */
  skip(): void {
    const tour = this.active()
    if (tour) tour.steps.forEach(step => this.markStepSeen(tour.id, step.target))
    this.clear()
  }

  /** Tears down the active tour without altering persistence (e.g. route change). */
  clear(): void {
    this.startToken++
    this.active.set(null)
    this.index.set(0)
  }

  /** Resolves the live DOM element for the current step, or `null`. */
  resolveCurrentElement(): HTMLElement | null {
    const step = this.currentStep()
    return step ? this.resolveElement(step) : null
  }

  /**
   * Clears persisted state. Pass a tour id to reset a single tour, or omit to
   * reset all. Exposed for debugging via `window.coachmarks`.
   */
  reset(tourId?: string): void {
    if (tourId) {
      this.seen.update(map => {
        const next = { ...map }
        delete next[tourId]
        return next
      })
    } else {
      this.seen.set({})
    }
    this.persistSeen()
  }

  // ---------------------------------------------------------------------------
  // Element resolution / visibility
  // ---------------------------------------------------------------------------

  resolveElement(step: CoachmarkStep): HTMLElement | null {
    const selector = /^[.#[]/.test(step.target) ? step.target : `[data-coachmark="${step.target}"]`
    let el: HTMLElement | null
    try {
      el = document.querySelector(selector) as HTMLElement | null
    } catch {
      return null
    }
    return el && this.isElementVisible(el) ? el : null
  }

  private isStepVisible(step: CoachmarkStep): boolean {
    return this.resolveElement(step) !== null
  }

  /**
   * An element is "visible" if it is rendered with a non-zero box and is not
   * hidden via display/visibility/opacity. Note: Angular control flow removes
   * non-rendered elements from the DOM, so absent targets are simply skipped.
   * Disabled elements are intentionally still considered visible.
   */
  private isElementVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return false
    const style = getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false
    return true
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  private markStepSeen(tourId: string, stepKey: string): void {
    if (this.hasSeenStep(tourId, stepKey)) return
    this.seen.update(map => ({
      ...map,
      [tourId]: { ...(map[tourId] ?? {}), [stepKey]: true },
    }))
    this.persistSeen()
  }

  private loadSeen(): SeenMap {
    try {
      const raw = localStorage.getItem(CoachmarkService.STORAGE_KEY)
      if (!raw) return {}
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const out: SeenMap = {}
      // Keep only well-formed nested entries (ignore any legacy boolean values).
      for (const [tourId, value] of Object.entries(parsed)) {
        if (value && typeof value === 'object') out[tourId] = value as Record<string, boolean>
      }
      return out
    } catch {
      return {}
    }
  }

  private persistSeen(): void {
    try {
      localStorage.setItem(CoachmarkService.STORAGE_KEY, JSON.stringify(this.seen()))
    } catch {
      // localStorage unavailable (private mode / quota) — fail silently.
    }
  }
}
